import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Import Cart Components
import { CartProvider, useCart } from "./context/CartContext";
import CartModal from "./components/CartModal";

// Import Wishlist Components
import { WishlistProvider, useWishlist } from "./context/WishlistContext";
import WishlistModal from "./components/WishlistModal";

// Import Filter Components
import FilterModal from "./components/FilterModal";

// Import Admin Components
import AdminLayout from "./components/Admin/AdminLayout";
import AdminLogin from "./components/Admin/AdminLogin";
import Dashboard from "./components/Admin/Dashboard";
import ProductManagement from "./components/Admin/ProductManagement";
import SubcategoryManagement from "./components/Admin/SubcategoryManagement";
import UserManagement from "./components/Admin/UserManagement";
import OrderManagement from "./components/Admin/OrderManagement";
import UserRegistration from "./components/UserRegistration";
import UserLogin from "./components/UserLogin";
import UserProfile from "./components/UserProfile";
import UserProfilePage from "./components/UserProfilePage";
import PayNowButton from "./components/PayNowButton";
import Checkout from "./components/Checkout";
import PaymentManagement from "./components/Admin/PaymentManagement";
import OrderNotification from "./components/OrderNotification";

// Import new component structure
import MainLayout from "./components/MainLayout";
import HomePage from "./components/HomePage";
import ProductsPage from "./components/ProductsPage";
import ProductDetail from "./components/ProductDetail";
import ProductDetailPage from "./components/ProductDetailPage";

// --- NEW: Import Policy Pages ---
import FaqPage from "./components/FaqPage";
import ShippingPolicyPage from "./components/ShippingPolicyPage";
import ReturnExchangePolicyPage from "./components/ReturnExchangePolicyPage";
import CancellationPolicyPage from "./components/CancellationPolicyPage";
import PrivacyPolicyPage from "./components/PolicyPageLayout";
import TermsOfServicePage from "./components/TermsAndConditionsPage";
// --- END NEW ---


// Use the API service that's already configured with proper environment variables
import api, { productsAPI } from './services/api';

// Main App Component with routing
const App = ({ onCartOpen, onWishlistOpen, selectedProduct, currentView, onProductClick, onBackToProducts, setCurrentView, sortBy, setSortBy, isFilterModalOpen, setIsFilterModalOpen, priceRange, setPriceRange, selectedMaterials, setSelectedMaterials, selectedSizes, setSelectedSizes }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    initializeApp();
  }, []);

  // Update category based on URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('home');
    } else if (path === '/dress') {
      setSelectedCategory('dresses');
      setSelectedSubcategory(null);
      setCurrentView('products');
      filterProducts('dresses', null, searchTerm);
    } else if (path === '/saree') {
      setSelectedCategory('sarees');
      setSelectedSubcategory(null);
      setCurrentView('products');
      filterProducts('sarees', null, searchTerm);
    } else if (path.startsWith('/dress/')) {
      const subcategory = path.split('/')[2];
      setSelectedCategory('dresses');
      setSelectedSubcategory(subcategory);
      setCurrentView('products');
      filterProducts('dresses', subcategory, searchTerm);
    } else if (path.startsWith('/saree/')) {
      const subcategory = path.split('/')[2];
      setSelectedCategory('sarees');
      setSelectedSubcategory(subcategory);
      setCurrentView('products');
      filterProducts('sarees', subcategory, searchTerm);
    } else if (path.startsWith('/material/')) {
      const material = path.split('/')[2];
      setSelectedCategory('materials');
      setSelectedSubcategory(material);
      setCurrentView('products');
      filterProducts('materials', material, searchTerm);
    }
  }, [location.pathname, products, searchTerm]);

  const initializeApp = async () => {
    try {
      // Load products using the configured API service
      const response = await productsAPI.getAll();
      // Backend returns { success, data, count, pagination }
      const productsData = response.data.data || response.data;
      console.log('Products loaded:', productsData);
      setProducts(productsData);
      setFilteredProducts(productsData);
      
      // Cart is now handled by CartContext
    } catch (error) {
      console.error('Error initializing app:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category, subcategory = null) => {
    try {
      console.log('Category selection:', { category, subcategory });
      setSelectedCategory(category);
      setSelectedSubcategory(subcategory);
      
      if (!category) {
        navigate('/');
        setCurrentView('home');
        setFilteredProducts(products || []);
      } else if (category === 'dresses') {
        if (subcategory) {
          navigate(`/dress/${subcategory}`);
        } else {
          navigate('/dress');
        }
        setCurrentView('products');
        filterProducts(category, subcategory, searchTerm);
      } else if (category === 'sarees') {
        if (subcategory) {
          navigate(`/saree/${subcategory}`);
        } else {
          navigate('/saree');
        }
        setCurrentView('products');
        filterProducts(category, subcategory, searchTerm);
      } else if (category === 'materials') {
        if (subcategory) {
          navigate(`/material/${subcategory}`);
        } else {
          navigate('/material');
        }
        setCurrentView('products');
        filterProducts(category, subcategory, searchTerm);
      }
    } catch (error) {
      console.error('Error in handleCategorySelect:', error);
      setFilteredProducts([]);
    }
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    if (products && Array.isArray(products)) {
      filterProducts(selectedCategory, selectedSubcategory, term);
    }
  };

  const handleSortChange = (sortType) => {
    setSortBy(sortType);
    if (products && Array.isArray(products)) {
      filterProducts(selectedCategory, selectedSubcategory, searchTerm);
    }
  };

  const sortProducts = (productsToSort, sortType) => {
    if (!productsToSort || !Array.isArray(productsToSort)) return productsToSort;
    
    const sorted = [...productsToSort];
    
    switch (sortType) {
      case "price-low-high":
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case "price-high-low":
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case "newest":
        return sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case "popular":
        // For now, sort by name as a placeholder for popularity
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      default:
        return sorted;
    }
  };

  const filterProducts = (category, subcategory, search) => {
    try {
      if (!products || !Array.isArray(products)) {
        console.log('No products array available');
        setFilteredProducts([]);
        return;
      }
      
      let filtered = products;
    
    console.log('Filtering products:', { category, subcategory, search, totalProducts: products.length });
    console.log('Sample product data:', products.slice(0, 2).map(p => ({ 
      name: p.name, 
      category: p.category, 
      subcategory: p.subcategory, 
      material: p.material 
    })));
    
    if (category) {
      if (category === "materials") {
        if (subcategory) {
          // Filter by specific material across all categories
          filtered = filtered.filter(p => 
            p.material && p.material.toLowerCase() === subcategory.toLowerCase()
          );
          console.log(`Filtered by material "${subcategory}":`, filtered.length, 'products');
        } else {
          // Show all products when materials category is selected but no specific material
          console.log(`Showing all products for materials category:`, filtered.length, 'products');
        }
      } else {
        // Filter by category (dresses/sarees)
        // The category should be in the subcategory field, and main category is usually "clothing"
        filtered = filtered.filter(p => 
          (p.subcategory && p.subcategory === category) || (p.category && p.category === category)
        );
        console.log(`Filtered by category "${category}":`, filtered.length, 'products');
        
        // If subcategory is specified, filter by material within that category
        if (subcategory) {
          filtered = filtered.filter(p => 
            p.material && p.material.toLowerCase() === subcategory.toLowerCase()
          );
          console.log(`Further filtered by material "${subcategory}":`, filtered.length, 'products');
        }
      }
    }
    
    if (search) {
      filtered = filtered.filter(p => 
        (p.name && p.name.toLowerCase().includes(search.toLowerCase())) ||
        (p.description && p.description.toLowerCase().includes(search.toLowerCase())) ||
        (p.material && p.material && p.material.toLowerCase().includes(search.toLowerCase()))
      );
      console.log(`Filtered by search "${search}":`, filtered.length, 'products');
    }

    // Apply price range filter
    filtered = filtered.filter(p => {
      const price = p.price || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    // Apply material filter
    if (selectedMaterials.length > 0) {
      filtered = filtered.filter(p => 
        p.material && selectedMaterials.includes(p.material.toLowerCase())
      );
    }

    // Apply size filter
    if (selectedSizes.length > 0) {
      filtered = filtered.filter(p => 
        p.sizes && p.sizes.some(size => selectedSizes.includes(size))
      );
    }
    
    console.log('Final filtered products:', filtered.length);
    
    // Apply sorting
    const sortedProducts = sortProducts(filtered, sortBy);
    setFilteredProducts(sortedProducts);
    } catch (error) {
      console.error('Error in filterProducts:', error);
      setFilteredProducts([]);
    }
  };

  const handleDebug = () => {
    try {
      console.log('=== PRODUCT DATA DEBUG ===');
      if (!products || !Array.isArray(products)) {
        console.log('No products data available');
        console.log('Products type:', typeof products);
        console.log('Products value:', products);
      } else {
        console.log('Total products:', products.length);
        console.log('Current filters:', { selectedCategory, selectedSubcategory });
        products.forEach((p, i) => {
          console.log(`Product ${i + 1}:`, {
            name: p?.name || 'No name',
            category: p?.category || 'No category',
            subcategory: p?.subcategory || 'No subcategory',
            material: p?.material || 'No material',
            matchesCategory: selectedCategory ? (p?.subcategory === selectedCategory || p?.category === selectedCategory) : 'N/A',
            matchesMaterial: selectedSubcategory ? (p?.material && p.material.toLowerCase() === selectedSubcategory.toLowerCase()) : 'N/A'
          });
        });
      }
      console.log('=== END DEBUG ===');
    } catch (error) {
      console.error('Error in debug function:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading MONVI...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Admin Routes */}
      <Route path="/register" element={<UserRegistration />} />
      <Route path="/login" element={<UserLogin />} />
      <Route path="/profile" element={<UserProfilePage />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<ProductManagement />} />
        <Route path="subcategories" element={<SubcategoryManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="orders" element={<OrderManagement />} />
        <Route path="payments" element={<PaymentManagement />} />
      </Route>

      {/* Main App Routes */}
      <Route path="/" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          {currentView === 'home' && (
            <HomePage 
              products={products} 
              onProductClick={onProductClick} 
            />
          )}
          
          {currentView === 'products' && (
            <ProductsPage 
              filteredProducts={filteredProducts}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              onFilterOpen={() => setIsFilterModalOpen(true)}
              onProductClick={onProductClick}
            />
          )}
          
          {currentView === 'detail' && selectedProduct && (
            <ProductDetail 
              product={selectedProduct} 
              onBack={onBackToProducts}
            />
          )}
        </MainLayout>
      } />

      {/* Category Routes */}
      <Route path="/dress" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductsPage 
            filteredProducts={filteredProducts}
            selectedCategory="dresses"
            selectedSubcategory={null}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onFilterOpen={() => setIsFilterModalOpen(true)}
            onProductClick={onProductClick}
          />
        </MainLayout>
      } />

      <Route path="/dress/:subcategory" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductsPage 
            filteredProducts={filteredProducts}
            selectedCategory="dresses"
            selectedSubcategory={selectedSubcategory}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onFilterOpen={() => setIsFilterModalOpen(true)}
            onProductClick={onProductClick}
          />
        </MainLayout>
      } />

      <Route path="/saree" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductsPage 
            filteredProducts={filteredProducts}
            selectedCategory="sarees"
            selectedSubcategory={null}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onFilterOpen={() => setIsFilterModalOpen(true)}
            onProductClick={onProductClick}
          />
        </MainLayout>
      } />

      <Route path="/saree/:subcategory" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductsPage 
            filteredProducts={filteredProducts}
            selectedCategory="sarees"
            selectedSubcategory={selectedSubcategory}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onFilterOpen={() => setIsFilterModalOpen(true)}
            onProductClick={onProductClick}
          />
        </MainLayout>
      } />

      <Route path="/material" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductsPage 
            filteredProducts={filteredProducts}
            selectedCategory="materials"
            selectedSubcategory={null}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onFilterOpen={() => setIsFilterModalOpen(true)}
            onProductClick={onProductClick}
          />
        </MainLayout>
      } />

      <Route path="/material/:material" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductsPage 
            filteredProducts={filteredProducts}
            selectedCategory="materials"
            selectedSubcategory={selectedSubcategory}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onFilterOpen={() => setIsFilterModalOpen(true)}
            onProductClick={onProductClick}
          />
        </MainLayout>
      } />

      {/* Product Detail Route */}
      <Route path="/product/:productId" element={
        <MainLayout
          onCategorySelect={handleCategorySelect}
          onSearchChange={handleSearchChange}
          onCartOpen={onCartOpen}
          onWishlistOpen={onWishlistOpen}
          onDebug={handleDebug}
        >
          <ProductDetailPage />
        </MainLayout>
      } />

      {/* --- NEW: Policy Page Routes --- */}
      <Route path="/components/faq" element={
        <MainLayout onCartOpen={onCartOpen} onWishlistOpen={onWishlistOpen}>
          <FaqPage />
        </MainLayout>
      } />
      <Route path="/components/shipping-policy" element={
        <MainLayout onCartOpen={onCartOpen} onWishlistOpen={onWishlistOpen}>
          <ShippingPolicyPage />
        </MainLayout>
      } />
      <Route path="/components/return-exchange-policy" element={
        <MainLayout onCartOpen={onCartOpen} onWishlistOpen={onWishlistOpen}>
          <ReturnExchangePolicyPage />
        </MainLayout>
      } />
      <Route path="/components/cancellation-policy" element={
        <MainLayout onCartOpen={onCartOpen} onWishlistOpen={onWishlistOpen}>
          <CancellationPolicyPage />
        </MainLayout>
      } />
      <Route path="/components/privacy-policy" element={
        <MainLayout onCartOpen={onCartOpen} onWishlistOpen={onWishlistOpen}>
          <PrivacyPolicyPage />
        </MainLayout>
      } />
      <Route path="/components/terms-of-service" element={
        <MainLayout onCartOpen={onCartOpen} onWishlistOpen={onWishlistOpen}>
          <TermsOfServicePage />
        </MainLayout>
      } />
      {/* --- END NEW --- */}

    </Routes>
  );
};

// Wrapper component to handle cart and wishlist modals
const AppWithCart = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const [sortBy, setSortBy] = useState("default");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const navigate = useNavigate();

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    // Navigate to product detail page
    navigate(`/product/${product._id || product.id}`);
  };

  const handleBackToProducts = () => {
    setSelectedProduct(null);
    // Navigate back to the previous page or home
    navigate(-1);
  };

  const handleFilterApply = (filters) => {
    setPriceRange(filters.priceRange);
    setSelectedMaterials(filters.materials);
    setSelectedSizes(filters.sizes);
    setIsFilterModalOpen(false);
  };

  const handleFilterClear = () => {
    setPriceRange({ min: 0, max: 100000 });
    setSelectedMaterials([]);
    setSelectedSizes([]);
  };

  return (
    <CartProvider>
      <WishlistProvider>
        <div>
          <App 
            onCartOpen={() => setIsCartOpen(true)} 
            onWishlistOpen={() => setIsWishlistOpen(true)}
            selectedProduct={selectedProduct}
            currentView={currentView}
            onProductClick={handleProductClick}
            onBackToProducts={handleBackToProducts}
            setCurrentView={setCurrentView}
            sortBy={sortBy}
            setSortBy={setSortBy}
            isFilterModalOpen={isFilterModalOpen}
            setIsFilterModalOpen={setIsFilterModalOpen}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            selectedMaterials={selectedMaterials}
            setSelectedMaterials={setSelectedMaterials}
            selectedSizes={selectedSizes}
            setSelectedSizes={setSelectedSizes}
          />
          <CartModal 
            isOpen={isCartOpen} 
            onClose={() => setIsCartOpen(false)} 
            onProductClick={handleProductClick}
          />
          <WishlistModal 
            isOpen={isWishlistOpen} 
            onClose={() => setIsWishlistOpen(false)} 
            onProductClick={handleProductClick}
          />
          <FilterModal 
            isOpen={isFilterModalOpen} 
            onClose={() => setIsFilterModalOpen(false)} 
            onApply={handleFilterApply}
            onClear={handleFilterClear}
            currentFilters={{
              priceRange,
              materials: selectedMaterials,
              sizes: selectedSizes
            }}
          />
        </div>
      </WishlistProvider>
    </CartProvider>
  );
};

// Wrap the entire app with BrowserRouter
const AppWithRouter = () => {
  return (
    <BrowserRouter>
      <OrderNotification />
      <AppWithCart />
    </BrowserRouter>
  );
};

export default AppWithRouter;